import { DeleteOutlined, EditOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Card, Input, Modal, Select, Switch, Table } from 'antd'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ItemResponseSspBeanObject, ListPagination, SspBean } from 'src/api/api'
import { StorageKey, storageReaderFactory, storageWriterFactory } from 'src/util/Storage'
import { useDebounce } from 'src/util/useDebounce'
import { toSort, useSorter } from 'src/util/useSorter'
import sspApi from './SspApi'

const Column = Table.Column
const Option = Select.Option

const storageWriter = storageWriterFactory('ssp')
const storageReader = storageReaderFactory('ssp')

interface Props { }

const SspList: React.FC<Props> = () => {

    const [data, setData] = useState<SspBean[]>([])
    const [pagination, setPagination] = useState<any>({
        defaultCurrent: 1,
        defaultPageSize: 10,
        current: 1
    })

    const [search, setSearch] = useState<string>()
    const debouncedSearch = useDebounce(search, 500)

    const [sspType, setSspType] = useState<SspBean.SspTypeEnum>()
    const [active, setActive] = useState<boolean>()

    const remove = (record: SspBean) => {
        Modal.confirm({
            title: `Delete ${record.name}?`,
            content: 'Are you sure?',
            cancelText: 'NO',
            okText: 'Yes, I\'m sure',
            onOk: async () => {
                await sspApi.delete(record.id as number)
                let page = pagination.current
                if (data.length == 1 && pagination.current > 1) {
                    page -= 1
                }
                loadData(page)
            }
        })
    }

    const activate = async (record: SspBean, index) => {
        await sspApi.activate(record.id)

        const bean = data[index]
        bean.active = !record.active

        setData([...data])
    }

    const loadData = async (page: number) => {

        const response = await sspApi.list(pagination.defaultPageSize, page, toSort(sorter), search, sspType, active)

        const array = response.data as Array<ItemResponseSspBeanObject>
        const sspBeans: SspBean[] = array.map(item => item.data as SspBean)

        if (sspBeans.length == 0 && page > 1) {
            loadData(page - 1)
        } else {
            const newPagination: ListPagination = response.pagination as ListPagination
            storageWriter.write(StorageKey.SearchParams, {
                page: newPagination.currentPage
            })

            setData(sspBeans)
            setPagination({ ...pagination, total: newPagination.total, current: newPagination.currentPage })
        }
    }

    const { sorter, handleTableChange } = useSorter(pagination, loadData)

    useEffect(() => {
        const searchParams = storageReader.read(StorageKey.SearchParams)
        const page = searchParams.page || pagination.current

        loadData(page)
    }, [debouncedSearch, sspType, active, sorter])

    return <Card size="small" className="form-card">
        <div className="list-filter-group">
            <Input prefix={<SearchOutlined />} placeholder="Search by ID, Name, Endpoint" onChange={e => setSearch(e.target.value || undefined)} />
            <Select placeholder="Filter by Type" onChange={setSspType} allowClear>
                {Object.values(SspBean.SspTypeEnum).map(type => <Option key={type} value={type}>{type}</Option>)}
            </Select>
            <Select placeholder="Filter by Active" onChange={setActive} allowClear>
                {Object.values(['Active', 'Inactive']).map(type => <Option key={type} value={type == 'Active'}>{type}</Option>)}
            </Select>
            <Link to={`/ssp/create`} className="create-new-link">
                <Button type="primary" htmlType="button">
                    Create new
                </Button>
            </Link>
        </div>
        <Table
            rowKey="id"
            size="middle"
            dataSource={data}
            pagination={{ ...pagination, size: 'default' }}
            onChange={handleTableChange}
        >
            <Column key="id" title="ID" dataIndex="id" sorter />
            <Column key="name" title="Name" dataIndex="name" sorter render={(text, record: SspBean) => <Link to={`/ssp/${record.id}/edit`}>{text}</Link>} />
            <Column key="endpoint" title="Endpoint" dataIndex="endpoint" />
            <Column key="sspType" title="Type" dataIndex="sspType" />
            <Column
                align="center"
                key="active"
                title="Active"
                dataIndex="active"
                render={(text, record: SspBean, index) => <Switch size="small" checked={record.active} onChange={e => activate(record, index)} />}
            />
            <Column align="center" key="action" title="Actions" render={(text: string, record: SspBean) =>
                <span>
                    <Link to={`/ssp/${record.id}/edit`}>
                        <Button type="text" >
                            <EditOutlined />
                        </Button>
                    </Link>
                    <Button type="text" danger onClick={() => remove(record)}>
                        <DeleteOutlined />
                    </Button>
                </span>
            } />
        </Table>
    </Card>
}

export default SspList


// WEBPACK FOOTER //
// ./src/component/app/ssp/SspList.tsx